// control-center/src/components/credentials/RevealPatDialog.tsx
// Hidden-by-default 30-second reveal dialog for PAT values.
// After 30s, the patValue is cleared from state entirely (not just hidden).
// revealEpoch forces the timer effect to re-fire even when revealed is already
// true (e.g. re-opening the dialog and clicking Reveal again resets the 30s clock).

import { useEffect, useState, useCallback } from 'react';

export interface RevealPatDialogProps {
  open: boolean;
  patName: string;
  vaultPath: string;
  patValue: string;
  onClose: () => void;
}

const REVEAL_DURATION_MS = 30_000;

export function RevealPatDialog({ open, patName, vaultPath, patValue, onClose }: RevealPatDialogProps) {
  const [revealed, setRevealed] = useState(false);
  const [revealEpoch, setRevealEpoch] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  // Auto-clear: patValue leaves state after 30s
  const [localValue, setLocalValue] = useState('');

  // Reset all state when the dialog is closed so re-opening starts fresh
  useEffect(() => {
    if (!open) {
      setLocalValue('');
      setRevealed(false);
      setCopyError(null);
    }
  }, [open]);

  const handleReveal = useCallback(() => {
    setLocalValue(patValue);
    setRevealed(true);
    setRevealEpoch(e => e + 1);  // forces timer effect to re-fire on every reveal
  }, [patValue]);

  useEffect(() => {
    if (!revealed) return;
    setSecondsLeft(Math.floor(REVEAL_DURATION_MS / 1000));
    const tick = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000);
    const timeout = setTimeout(() => {
      setRevealed(false);
      setLocalValue('');  // Clear from state — not just hidden
    }, REVEAL_DURATION_MS);
    return () => {
      clearInterval(tick);
      clearTimeout(timeout);
    };
  }, [revealed, revealEpoch]);  // revealEpoch dependency ensures re-opens restart the timer

  if (!open) return null;

  const onCopy = async () => {
    if (!localValue) return;
    try {
      await navigator.clipboard.writeText(localValue);
      setCopied(true);
      setCopyError(null);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyError('Copy failed — select the text manually and copy with Ctrl+C / Cmd+C');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reveal-dialog-title"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
      }}
    >
      <div
        style={{
          background: '#1e293b', color: '#e2e8f0', borderRadius: 8, padding: '1.5rem',
          maxWidth: 480, width: '100%', boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
        }}
      >
        <h3 id="reveal-dialog-title" style={{ margin: '0 0 0.5rem', color: '#7c3aed' }}>
          Mint succeeded — {patName}
        </h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#94a3b8' }}>
          Stored at <code style={{ fontSize: '0.75rem', color: '#7c3aed' }}>{vaultPath}</code>
        </p>
        <div
          style={{
            background: '#0f172a', borderRadius: 6, padding: '0.75rem',
            marginBottom: '0.75rem', minHeight: 48, display: 'flex', alignItems: 'center',
          }}
        >
          {revealed && localValue ? (
            <pre
              data-testid="pat-value"
              style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', color: '#4ade80' }}
            >
              {localValue}
            </pre>
          ) : (
            <button
              onClick={handleReveal}
              style={{
                background: '#312e81', color: '#a5b4fc', border: 'none', borderRadius: 4,
                padding: '0.4rem 1rem', cursor: 'pointer', fontSize: '0.875rem',
              }}
            >
              Reveal token (30s)
            </button>
          )}
        </div>
        {revealed && (
          <div style={{ fontSize: '0.75rem', color: '#f59e0b', marginBottom: '0.5rem' }}>
            Auto-clears in {secondsLeft}s
          </div>
        )}
        {copyError && (
          <div style={{ fontSize: '0.75rem', color: '#f87171', marginBottom: '0.5rem' }}>
            {copyError}
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            onClick={onCopy}
            disabled={!revealed || !localValue}
            style={{
              background: copied ? '#166534' : '#1e3a5f', color: copied ? '#86efac' : '#93c5fd',
              border: 'none', borderRadius: 4, padding: '0.4rem 1rem', cursor: 'pointer', fontSize: '0.875rem',
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            onClick={onClose}
            style={{
              background: '#374151', color: '#d1d5db', border: 'none', borderRadius: 4,
              padding: '0.4rem 1rem', cursor: 'pointer', fontSize: '0.875rem',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
